import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import { CrmLead, CrmProspectChoice, CrmVendorChoice, aliasForCompany } from '../../../common/models/crm.model';

type Mode = 'existing' | 'new';
const P = 'ADMIN_CRM.PROSPECT.';
/** Wait this long after the last keystroke before searching. */
const SEARCH_DELAY = 250;

interface CrmVendorDialogProps {
  /** The lead to give a vendor to; null closes the dialog. */
  lead: CrmLead | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (choice: CrmProspectChoice) => void;
}

/**
 * Asks how a lead gets its vendor: an existing vendor (a demo already made for
 * it, or a customer), or a new prospect under an alias. A vendor belongs to one
 * lead, so those linked to another lead are shown but can't be picked.
 */
export const CrmVendorDialog: React.FC<CrmVendorDialogProps> = ({ lead, busy, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const api = useCrmApi();
  const [mode, setMode] = useState<Mode>('existing');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CrmVendorChoice[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [alias, setAlias] = useState('');
  const [name, setName] = useState('');

  // A fresh start for each lead: its company is the first search and the new prospect's defaults.
  useEffect(() => {
    if (!lead) return;
    setMode('existing');
    setQ(lead.company);
    setPicked(null);
    setAlias(aliasForCompany(lead.company));
    setName(lead.company);
  }, [lead]);

  useEffect(() => {
    if (!lead || mode !== 'existing') return undefined;
    let live = true;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const found = await api.searchVendors(q.trim());
        if (live) setResults(found || []);
      } catch {
        if (live) setResults([]);
      } finally {
        if (live) setSearching(false);
      }
    }, SEARCH_DELAY);
    return () => { live = false; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mode, lead]);

  const takenBy = (v: CrmVendorChoice) => (v.lead && v.lead._id !== lead?._id ? v.lead : null);
  const canConfirm = mode === 'existing' ? !!picked : !!alias.trim();
  const confirm = () => {
    if (!canConfirm || busy) return;
    onConfirm(mode === 'existing' ? { vendorId: picked! } : { alias: alias.trim().toLowerCase(), name: name.trim() });
  };

  return (
    <Dialog open={!!lead} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`${P}CHOOSE_TITLE`)}</DialogTitle>
          <DialogDescription>{t(`${P}CHOOSE_DESCRIPTION`, { company: lead?.company })}</DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
          <TabsList className="w-full">
            <TabsTrigger value="existing" data-testid="crm-vendor-mode-existing">{t(`${P}MODE_EXISTING`)}</TabsTrigger>
            <TabsTrigger value="new" data-testid="crm-vendor-mode-new">{t(`${P}MODE_NEW`)}</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === 'existing' ? (
          <div className="flex min-w-0 flex-col gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t(`${P}SEARCH`)}
              aria-label={t(`${P}SEARCH`)}
            />
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto" role="listbox" aria-label={t(`${P}MODE_EXISTING`)}>
              {searching && !results.length && (
                <p className="flex items-center gap-2 px-1 py-3 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />{t(`${P}SEARCHING`)}
                </p>
              )}
              {!searching && !results.length && <p className="px-1 py-3 text-sm text-muted-foreground">{t(`${P}NO_VENDOR`)}</p>}
              {results.map((v) => {
                const taken = takenBy(v);
                const selected = picked === v._id;
                return (
                  <button
                    key={v._id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={!!taken}
                    onClick={() => setPicked(v._id)}
                    data-testid={`crm-vendor-option-${v._id}`}
                    className={cn(
                      'flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      selected ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:bg-muted',
                      taken && 'cursor-not-allowed opacity-60 hover:bg-transparent',
                    )}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{v.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {v.alias}{taken && ` · ${t(`${P}TAKEN_BY`, { company: taken.company })}`}
                      </span>
                    </span>
                    <Badge variant={v.kind === 'prospect' ? 'secondary' : 'outline'}>
                      {t(v.kind === 'prospect' ? `${P}KIND_PROSPECT` : `${P}KIND_CUSTOMER`)}
                    </Badge>
                    {selected && <Check className="size-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="crm-new-alias" className="text-xs text-muted-foreground">{t(`${P}ALIAS`)}</Label>
              <Input id="crm-new-alias" value={alias} onChange={(e) => setAlias(e.target.value)} className="font-mono" />
              <p className="text-xs text-muted-foreground">{t(`${P}ALIAS_HINT`, { alias: alias.trim().toLowerCase() || '…' })}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="crm-new-name" className="text-xs text-muted-foreground">{t(`${P}NAME`)}</Label>
              <Input id="crm-new-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('ADMIN_CRM.COMMON.CANCEL')}</Button>
          <Button onClick={confirm} disabled={!canConfirm || busy} data-testid="crm-vendor-confirm">
            {busy && <Loader2 data-icon="inline-start" className="animate-spin" />}
            {t(mode === 'existing' ? `${P}LINK` : `${P}CREATE_NEW`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
